// Site Texts Patch 1 — Lightweight admin-editable site-text hook.
//
// Fetches /api/settings/site-texts once per session (5 min TTL), caches
// in memory + localStorage, and returns a `t(key, fallback)` helper that
// ALWAYS resolves to a string — never undefined — so pages don't break if
// a key is missing / blank / not yet published.
//
// Usage:
//   const t = useSiteTexts();
//   <h1>{t("trial_booking_heading", "Book a Trial Fitting")}</h1>

import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "mahra_site_texts_v1";
const TTL_MS = 5 * 60 * 1000; // 5 min

let inMemory = null;   // { texts, ts }
let inFlight = null;   // Promise dedupe

function readCache() {
  if (inMemory && (Date.now() - inMemory.ts) < TTL_MS) return inMemory.texts;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.ts && (Date.now() - parsed.ts) < TTL_MS && parsed.texts) {
      inMemory = parsed;
      return parsed.texts;
    }
  } catch { /* ignore */ }
  return null;
}

async function fetchTexts() {
  const cached = readCache();
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = fetch(`${API}/api/settings/site-texts`)
    .then((r) => r.ok ? r.json() : { texts: {} })
    .then((body) => {
      const texts = body?.texts || {};
      const now = Date.now();
      inMemory = { texts, ts: now };
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ texts, ts: now })); } catch { /* ignore */ }
      return texts;
    })
    .catch(() => ({}))
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * `t(key, fallback?)` — resolves an admin-editable string.
 *
 * If the key isn't in the admin settings yet, OR the admin has cleared
 * the value to an empty string, the fallback (or the key itself) is
 * returned so the UI never renders `undefined` / blank.
 */
export function useSiteTexts() {
  const [texts, setTexts] = useState(() => readCache() || {});

  useEffect(() => {
    let cancelled = false;
    fetchTexts().then((next) => {
      if (!cancelled) setTexts(next || {});
    });
    return () => { cancelled = true; };
  }, []);

  return function t(key, fallback) {
    const v = texts[key];
    if (typeof v === "string" && v.trim() !== "") return v;
    return fallback !== undefined ? fallback : key;
  };
}

// For testing / integration surfaces that want to force a refresh
// (e.g. after admin saves in AdminSiteTexts.jsx).
export function invalidateSiteTextsCache() {
  inMemory = null;
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

export default useSiteTexts;
